/**
 * The admin end of the TournamentRoom WebSocket.
 *
 * The boards panel needs two things REST cannot give it: who is throwing right
 * now, and scores that move without polling. Neither is in /boards — that
 * endpoint answers "who is on which board", which is the question that survives
 * a reload. Whose turn it is only exists in the visit rows, and the Durable
 * Object is the only thing that streams those.
 *
 * SYNC-ON-EVENT, not patch-on-event. Every mutating broadcast triggers a
 * `sync` and the room answers with a whole snapshot. Applying the deltas by
 * hand would mean reimplementing checkout, bust and leg-completion arithmetic
 * on the client, and a client that disagrees with the room about a score is
 * worse than one that waits 50ms for the truth. A night runs a handful of
 * boards; the snapshot is small.
 *
 * The room is a separate Worker from Pages. Without `npm run dev:rooms` the
 * socket simply never opens, which is why every consumer renders a
 * disconnected state rather than assuming a snapshot is on its way.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** Broadcasts that mean the board state has moved. */
const MUTATING = new Set([
  'visit_recorded',
  'visit_corrected',
  'visit_deleted',
  'leg_completed',
  'match_started',
  'match_completed',
  'board_paired',
  'scorer_disconnected',
  'scorer_reconnected',
  // A board's queue depth moved. Only sent when it actually changes, so this
  // does not resync the room every thirty seconds for a heartbeat.
  'scorer_queue_changed',
  // A visit was parked for review: the snapshot's heldVisits count has moved.
  'visit_held',
  'tournament_completed',
  // A whole round of matches that did not exist a moment ago now occupies every
  // board.
  'knockout_generated',
  // The draw just ran: there were no boards a moment ago and now there are.
  'draw_generated',
  // Only the TV acts on the takeover itself, but the snapshot carries it and
  // the admin dashboard reads its own button state back from there.
  'total_raised_changed',
]);

const RECONNECT_MS = 3000;

/**
 * Whose throw it is in the live leg, or null.
 *
 * Players alternate from the leg's starter, so the count of visits each has
 * thrown is the whole answer: level means the starter is up, one ahead means
 * the other player is. Derived rather than stored because nothing stores it —
 * there is no `current_thrower` column anywhere in the schema.
 */
export function throwingEntryId(match) {
  if (!match || match.status !== 'live') return null;

  const leg = (match.legs ?? []).find((l) => l.id === match.currentLegId);
  if (!leg) return null;

  const starter = leg.starting_player_id;
  if (starter == null) return null;

  const other = starter === match.player1_id ? match.player2_id : match.player1_id;
  const thrown = (id) => (leg.visits ?? []).filter((v) => v.player_id === id).length;

  return thrown(starter) === thrown(other) ? starter : other;
}

/**
 * Three-dart average per entry across a whole match, keyed by entry id.
 *
 * Busts are INCLUDED — a bust is three darts thrown for nothing, and excluding
 * them would flatter the average. This matches visit_stats in getGroupStandings,
 * which also sums every visit, so a board card and the standings table cannot
 * quote different numbers for the same player.
 */
export function matchAverages(match) {
  const totals = new Map();

  for (const leg of match?.legs ?? []) {
    for (const visit of leg.visits ?? []) {
      const t = totals.get(visit.player_id) ?? { score: 0, darts: 0 };
      t.score += visit.score;
      t.darts += visit.darts_used;
      totals.set(visit.player_id, t);
    }
  }

  const averages = {};
  for (const [entryId, { score, darts }] of totals) {
    averages[entryId] = darts > 0 ? (score / darts) * 3 : 0;
  }
  return averages;
}

/**
 * Live room state for one tournament.
 *
 * Returns { status, snapshot, completedByBoard }, where completedByBoard holds
 * the match that just finished on each board. The room's boardState only ever
 * reports a live or pending match, so without this a completed match — the only
 * time averages are shown — would vanish the instant it ended.
 *
 * `type` is the room's connection kind rather than a detail of this hook: the
 * room filters scorers by board number and hands every other kind the whole
 * tournament, so tv, live and admin differ only in what the door logs them in
 * as. It defaults to admin so the dashboard's call sites are unchanged.
 */
export function useTournamentRoom(venueId, tournamentId, type = 'admin') {
  const [status, setStatus]     = useState('connecting');
  const [snapshot, setSnapshot] = useState(null);
  const [completedByBoard, setCompletedByBoard] = useState({});

  // The room builds the bracket itself the moment the last group match lands on
  // a clean split, so this can arrive with nobody having pressed anything. The
  // socket carries the boards; the bracket, fixtures and standings are REST, so
  // a page showing those has to be told to go and fetch them again.
  const [knockoutGenerated, setKnockoutGenerated] = useState(null);

  const socketRef  = useRef(null);
  const retryRef   = useRef(null);
  const closedRef  = useRef(false);

  /**
   * The final figures for the match that just ended on each board, from the
   * room's own `match_completed`.
   *
   * THE HELD SNAPSHOT IS ALWAYS ONE LEG SHORT. The visit that wins the
   * deciding leg also ends the match, so the last snapshot in which the match
   * was still on a board was taken BEFORE that visit. Reading legs and
   * averages off it shows a 4-0 final as 3-0 — the arithmetic is right and the
   * input is stale.
   *
   * `match_completed` carries getMatchStats for exactly this match, so the
   * true numbers are already in hand and nothing has to be re-fetched.
   */
  const finalsRef = useRef({});

  /** Fold a new snapshot in, remembering any match that has just ended. */
  const applySnapshot = useCallback((next) => {
    setSnapshot((prev) => {
      const previousBoards = new Map(
        (prev?.boards ?? []).map((b) => [b.boardNumber, b])
      );

      setCompletedByBoard((held) => {
        const updated = { ...held };

        for (const board of next.boards ?? []) {
          const before = previousBoards.get(board.boardNumber);

          if (board.match) {
            // Something is live again, so the finished card has been replaced.
            delete updated[board.boardNumber];
            delete finalsRef.current[board.boardNumber];
          } else if (before?.match && before.match.status === 'live') {
            // It was live a moment ago and is not on the board now: it ended.
            const final = finalsRef.current[board.boardNumber];
            const matches = final && final.matchId === before.match.id;

            updated[board.boardNumber] = {
              ...before.match,
              status: 'completed',
              // Guarded on the match id: a board whose next match ended before
              // this snapshot landed must not be given the previous one's
              // score.
              ...(matches ? { legsWon: final.legsWon, finalAverages: final.averages } : {}),
            };
          }
        }

        return updated;
      });

      return next;
    });
  }, []);

  useEffect(() => {
    if (venueId == null || tournamentId == null) return undefined;

    closedRef.current = false;

    const connect = () => {
      if (closedRef.current) return;

      const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url =
        `${scheme}//${window.location.host}` +
        `/api/venues/${venueId}/tournaments/${tournamentId}/live?type=${type}`;

      let socket;
      try {
        socket = new WebSocket(url);
      } catch {
        setStatus('disconnected');
        return;
      }

      socketRef.current = socket;

      socket.onopen = () => setStatus('connected');

      socket.onmessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return; // Not ours to interpret; the room only ever sends JSON.
        }

        // The room's envelope is flat — send() spreads the payload alongside
        // the type rather than nesting it under `payload`.
        if (message.type === 'connected' || message.type === 'sync_state') {
          if (message.snapshot) applySnapshot(message.snapshot);
          return;
        }

        // Recorded BEFORE the sync goes out, so the snapshot that answers it
        // already finds the final figures waiting for it.
        if (message.type === 'match_completed' && message.stats?.players) {
          finalsRef.current[message.boardNumber] = {
            matchId:  message.matchId,
            legsWon:  Object.fromEntries(
              message.stats.players.map((p) => [p.entryId, p.legsWon])
            ),
            averages: Object.fromEntries(
              message.stats.players.map((p) => [p.entryId, p.threeDartAvg])
            ),
          };
        }

        if (MUTATING.has(message.type) && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'sync' }));
        }

        // Set AFTER the sync request, and held as the payload rather than a
        // boolean or a flag: a fresh object each time is what makes a
        // consumer's effect fire again if this ever happens twice, and the
        // payload carries the bracket summary a notice would want to show.
        if (message.type === 'knockout_generated') setKnockoutGenerated(message);
      };

      socket.onclose = () => {
        if (closedRef.current) return;
        setStatus('disconnected');
        // The room lives in a Worker that may simply not be running in dev.
        // Retrying quietly is right; surfacing an error every 3s is not.
        retryRef.current = setTimeout(connect, RECONNECT_MS);
      };

      socket.onerror = () => socket.close();
    };

    connect();

    return () => {
      closedRef.current = true;
      clearTimeout(retryRef.current);
      socketRef.current?.close();
    };
  }, [venueId, tournamentId, type, applySnapshot]);

  return { status, snapshot, completedByBoard, knockoutGenerated };
}
